import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Chip,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
} from '@mui/material';
import { Close, History } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { UsersApi } from '@/api/clients';
import {
  USER_HISTORY_ACTION,
  getUserTypeLabel,
  getMfaTypeLabel,
} from '@/views/Facilities/UserManagement/components/userHistory.utils';

const InternalUserHistory = ({ open, onClose, user }) => {
  const { t } = useTranslation();
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open && user) {
      fetchHistory();
    }
  }, [open, user]);

  const fetchHistory = async () => {
    setLoading(true);
    try {
      const response = await UsersApi.getFacilityUserHistory(user.id);
      setHistory(response.data?.data?.history_entries || []);
    } catch (error) {
      console.error('Error fetching user history:', error);
      toast.error(t('internalUsers.history.fetchError'));
    } finally {
      setLoading(false);
    }
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getActionInfo = (action) => {
    const actionMap = {
      [USER_HISTORY_ACTION.ROLE_CHANGED]: {
        label: t('internalUsers.history.actions.roleChanged'),
        color: 'primary',
        sx: {
          '& .MuiChip-label': {
            color: 'white',
          },
        },
      },
      [USER_HISTORY_ACTION.ENABLE]: {
        label: t('internalUsers.history.actions.enabled'),
        color: 'success',
      },
      [USER_HISTORY_ACTION.DISABLE]: {
        label: t('internalUsers.history.actions.disabled'),
        color: 'warning',
      },
      [USER_HISTORY_ACTION.CREATE]: {
        label: t('internalUsers.history.actions.created'),
        color: 'info',
      },
      [USER_HISTORY_ACTION.UPDATE]: {
        label: t('internalUsers.history.actions.updated'),
        color: 'info',
      },
      [USER_HISTORY_ACTION.USER_CREATED]: {
        label: t('internalUsers.history.actions.userCreated'),
        color: 'success',
      },
      [USER_HISTORY_ACTION.METADATA_CHANGED]: {
        label: t('internalUsers.history.actions.metadataChanged'),
        color: 'info',
      },
      [USER_HISTORY_ACTION.PERMISSIONS_CHANGED]: {
        label: t('internalUsers.history.actions.permissionsChanged'),
        color: 'warning',
      },
      [USER_HISTORY_ACTION.MFA_CHANGED]: {
        label: t('internalUsers.history.actions.mfaChanged'),
        color: 'info',
      },
      [USER_HISTORY_ACTION.FACILITY_ACCESS_CHANGED]: {
        label: t('internalUsers.history.actions.facilityAccessChanged'),
        color: 'primary',
        sx: {
          '& .MuiChip-label': {
            color: 'white',
          },
        },
      },
      [USER_HISTORY_ACTION.ROLE_CREATED]: {
        label: t('internalUsers.history.actions.roleCreated'),
        color: 'success',
      },
      [USER_HISTORY_ACTION.ROLE_UPDATED]: {
        label: t('internalUsers.history.actions.roleUpdated'),
        color: 'info',
      },
      [USER_HISTORY_ACTION.ROLE_DELETED]: {
        label: t('internalUsers.history.actions.roleDeleted'),
        color: 'error',
      },
    };

    return actionMap[action] || { label: action, color: 'default' };
  };

  const getShortChanges = (entry) => {
    const { action, previous_value, new_value } = entry;

    switch (action) {
      case USER_HISTORY_ACTION.USER_CREATED:
        return new_value?.email || 'New user';
      case USER_HISTORY_ACTION.ENABLE:
        return 'Active';
      case USER_HISTORY_ACTION.DISABLE:
        return 'Inactive';
      case USER_HISTORY_ACTION.ROLE_CHANGED:
        if (entry.previous_role_name && entry.new_role_name) {
          return `${entry.previous_role_name} → ${entry.new_role_name}`;
        }
        return entry.new_role_name || 'Role changed';
      case USER_HISTORY_ACTION.MFA_CHANGED: {
        const from = getMfaTypeLabel(previous_value?.mfa_type);
        const to = getMfaTypeLabel(new_value?.mfa_type);
        return `${from} → ${to}`;
      }
      case USER_HISTORY_ACTION.METADATA_CHANGED:
        if (
          previous_value?.language_preference !== new_value?.language_preference
        ) {
          return `Language: ${new_value?.language_preference?.toUpperCase()}`;
        }
        if (previous_value?.name !== new_value?.name) {
          return `Name: ${new_value?.name}`;
        }
        return 'Info updated';
      case USER_HISTORY_ACTION.PERMISSIONS_CHANGED:
        return 'Permissions updated';
      case USER_HISTORY_ACTION.FACILITY_ACCESS_CHANGED:
        return `Facilities: ${new_value?.facility_ids || 'updated'}`;
      default:
        return null;
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth='lg' fullWidth>
      <DialogTitle sx={{ mb: 2 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <History />
            <Typography variant='h6'>
              {t('internalUsers.history.title', { name: user?.name })}
            </Typography>
          </Box>
          <Tooltip title={t('common.close')}>
            <IconButton onClick={onClose} size='small'>
              <Close />
            </IconButton>
          </Tooltip>
        </Box>
      </DialogTitle>

      <DialogContent>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : history.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography color='text.secondary'>
              {t('internalUsers.history.noHistory')}
            </Typography>
          </Box>
        ) : (
          <TableContainer component={Paper} variant='outlined'>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>
                    {t('internalUsers.history.columns.timestamp')}
                  </TableCell>
                  <TableCell>
                    {t('internalUsers.history.columns.action')}
                  </TableCell>
                  <TableCell>
                    {t('internalUsers.history.columns.changedBy')}
                  </TableCell>
                  <TableCell>
                    {t('internalUsers.history.columns.changes')}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {history.map((entry) => {
                  const actionInfo = getActionInfo(entry.action);
                  return (
                    <TableRow key={entry.id} hover>
                      <TableCell>
                        <Typography variant='body2'>
                          {formatTimestamp(entry.changed_at)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={actionInfo.label}
                          color={actionInfo.color}
                          size='small'
                          sx={actionInfo.sx}
                        />
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant='body2'>
                            {entry.changed_by?.name ||
                              entry.changed_by?.email ||
                              getUserTypeLabel(entry.changed_by_user_type) ||
                              ''}
                          </Typography>
                          {entry.changed_by?.name && entry.changed_by?.email && (
                            <Typography
                              variant='caption'
                              color='text.secondary'>
                              {entry.changed_by.email}
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant='body2' color='text.secondary'>
                          {getShortChanges(entry)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>

      <DialogActions sx={{ justifyContent: 'flex-end', px: 3, pb: 2 }}>
        <Button onClick={onClose}>{t('common.close')}</Button>
      </DialogActions>
    </Dialog>
  );
};

export default InternalUserHistory;
